import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import {
  adminListMessages,
  adminSetMessageStatus,
  adminDeleteMessage,
  getAdminToken,
  clearAdminToken,
} from "../lib/api";
import { formatDate } from "../lib/utils-tv";

const TABS = [
  { key: "new", label: "new" },
  { key: "read", label: "read" },
  { key: "archived", label: "archived" },
  { key: "all", label: "all" },
];

const SUBJECT_LABEL = {
  general: "general",
  correction: "correction",
  removal: "removal",
  press: "press",
  other: "other",
};

export default function AdminInbox() {
  const nav = useNavigate();
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await adminListMessages(tab === "all" ? undefined : tab);
      setItems(rows || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        nav("/admin/login");
        return;
      }
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
    // adminListMessages/clearAdminToken are stable module imports; setState setters
    // are stable per React contract. Excluded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, nav]);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const setStatus = async (id, status) => {
    try {
      await adminSetMessageStatus(id, status);
      toast.success(`marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    try {
      await adminDeleteMessage(id);
      toast.success("deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-inbox-page">
      <Seo title="Admin inbox" path="/admin/inbox" noindex />
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between border-b border-black pb-2">
          <h1 className="text-lg font-bold">{">"}_ admin / inbox</h1>
          <div className="flex items-center gap-3 text-[12px]">
            <Link to="/admin" className="tv-link">← back to tools</Link>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="inbox-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tv-cat-pill ${tab === t.key ? "active" : ""}`}
              data-testid={`inbox-tab-${t.key}`}
            >
              [{t.label}]
            </button>
          ))}
          <button className="tv-cat-pill ml-auto" onClick={load} data-testid="inbox-refresh">[refresh]</button>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-[13px] text-[#888] py-6" data-testid="inbox-loading">loading...</div>
          ) : items.length === 0 ? (
            <div className="text-[13px] text-[#888] py-6" data-testid="inbox-empty">no messages.</div>
          ) : (
            <ul className="list-none p-0 m-0">
              {items.map((m) => (
                <li key={m.id} className="border border-black bg-white p-3 mb-3" data-testid={`msg-${m.id}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="text-[12px]">
                      <span className="font-bold">[{SUBJECT_LABEL[m.subject] || m.subject}]</span>
                      {" "}
                      <span className="text-[#888]">{formatDate(m.created_at)}</span>
                      {" · "}
                      <a href={`mailto:${m.email}`} className="tv-link">{m.email}</a>
                      {m.name && <span className="text-[#555]"> ({m.name})</span>}
                    </div>
                    <div className="text-[11px] text-[#888]">
                      status: <span className={m.status === "new" ? "text-[#b00020] font-bold" : "text-[#000]"}>{m.status}</span>
                    </div>
                  </div>
                  {m.tool_url && (
                    <div className="text-[11px] mt-1">
                      tool: <a href={m.tool_url} target="_blank" rel="noopener noreferrer" className="tv-link">{m.tool_url}</a>
                    </div>
                  )}
                  <div className="mt-2 text-[13px] whitespace-pre-wrap">{m.message}</div>
                  <div className="mt-3 flex flex-wrap gap-3 text-[12px]">
                    {m.status !== "read" && (
                      <button className="tv-action-link" onClick={() => setStatus(m.id, "read")} data-testid={`read-${m.id}`}>[mark read]</button>
                    )}
                    {m.status !== "archived" && (
                      <button className="tv-action-link" onClick={() => setStatus(m.id, "archived")} data-testid={`archive-${m.id}`}>[archive]</button>
                    )}
                    {m.status !== "new" && (
                      <button className="tv-action-link" onClick={() => setStatus(m.id, "new")} data-testid={`renew-${m.id}`}>[back to new]</button>
                    )}
                    <button className="tv-action-link danger" onClick={() => del(m.id)} data-testid={`del-${m.id}`}>[delete]</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
